import type { SeoConfig } from '@shopify/hydrogen';
import type { MetaDescriptor } from 'react-router';

/**
 * Type guard to check if an unknown value is a valid SeoConfig
 * Handles the complex Sanity-generated types and ensures type safety
 */
export const isSeoConfig = (value: unknown): value is SeoConfig => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  // Check that all properties are of the expected types
  const validTitle = obj.title === undefined || typeof obj.title === 'string';
  const validDescription = obj.description === undefined || typeof obj.description === 'string';
  const validUrl = obj.url === undefined || typeof obj.url === 'string';
  const validTitleTemplate = obj.titleTemplate === undefined || typeof obj.titleTemplate === 'string';

  // Media can be string, object, or array
  const validMedia =
    obj.media === undefined ||
    typeof obj.media === 'string' ||
    Array.isArray(obj.media) ||
    (typeof obj.media === 'object' && obj.media !== null);

  // Robots should be an object with boolean properties
  const validRobots = obj.robots === undefined || (typeof obj.robots === 'object' && obj.robots !== null);

  // JsonLd can be object or array
  const validJsonLd = obj.jsonLd === undefined || typeof obj.jsonLd === 'object';

  return validTitle && validDescription && validUrl && validTitleTemplate && validMedia && validRobots && validJsonLd;
};

/**
 * Safe wrapper for getSeoMeta that accepts unknown types and validates them
 * Use this in meta functions to handle complex Sanity-generated types safely
 *
 * Usage:
 * ```tsx
 * export const meta = ({ loaderData }: Route.MetaArgs) => {
 *   return getSeoMetaSafe(loaderData?.seo);
 * };
 * ```
 */
export const getSeoMetaSafe = (...configs: unknown[]): MetaDescriptor[] => {
  const validConfigs = configs.filter(isSeoConfig);
  return getSeoMeta(...validConfigs);
};

/**
 * React Router 7 replacement for Hydrogen's getSeoMeta function
 * Combines multiple SEO configs (e.g. root + page) and converts to MetaDescriptor array
 * Later configs override earlier ones, except for jsonLd which gets concatenated
 * Note that as of 27/10/2025, the matches param from the React Router meta function can not be used due to type complexity issues that cause major TypeScript performance degradation. This means that this function should not be used with any matches data.
 *
 * Usage:
 * ```tsx
 * // Single config (use getSeoMetaSafe for unknown types)
 * return getSeoMeta(validSeoConfig);
 *
 * // Multiple configs (root inherits to page)
 * return getSeoMeta(rootSeo, pageSeo);
 * ```
 */
export function getSeoMeta(...configs: SeoConfig[]): MetaDescriptor[] {
  const validConfigs = configs.filter(Boolean);

  if (validConfigs.length === 0) {
    return [];
  }

  // Merge configs if multiple (later overrides earlier, except jsonLd)
  const mergedConfig =
    validConfigs.length === 1
      ? validConfigs[0]
      : validConfigs.reduce<SeoConfig>((acc, current) => {
          // Handle jsonLd concatenation
          let combinedJsonLd = acc.jsonLd;
          if (current.jsonLd) {
            if (combinedJsonLd) {
              const existingArray = Array.isArray(combinedJsonLd) ? combinedJsonLd : [combinedJsonLd];
              const currentArray = Array.isArray(current.jsonLd) ? current.jsonLd : [current.jsonLd];
              combinedJsonLd = [...existingArray, ...currentArray];
            } else {
              combinedJsonLd = current.jsonLd;
            }
          }

          return { ...acc, ...current, jsonLd: combinedJsonLd };
        }, {});

  const metaTags: MetaDescriptor[] = [];
  const { title, titleTemplate, description, url, media, robots, jsonLd } = mergedConfig;

  // Title handling with template support
  if (title) {
    const finalTitle = titleTemplate ? titleTemplate.replace('%s', title) : title;
    metaTags.push({ title: finalTitle });
    metaTags.push({ property: 'og:title', content: finalTitle });
  }

  // Description
  if (description) {
    metaTags.push({ name: 'description', content: description });
    metaTags.push({ property: 'og:description', content: description });
  }

  // Canonical URL
  if (url) {
    metaTags.push({ tagName: 'link', rel: 'canonical', href: url });
    metaTags.push({ property: 'og:url', content: url });
  }

  // Media handling
  if (media) {
    if (typeof media === 'string') {
      metaTags.push({ property: 'og:image', content: media });
    } else if (Array.isArray(media)) {
      media.forEach((item) => {
        if (typeof item === 'string') {
          metaTags.push({ property: 'og:image', content: item });
        } else if (item?.url) {
          metaTags.push({ property: 'og:image', content: item.url });
          if (item.altText) {
            metaTags.push({ property: 'og:image:alt', content: item.altText });
          }
          if (item.width) {
            metaTags.push({ property: 'og:image:width', content: String(item.width) });
          }
          if (item.height) {
            metaTags.push({ property: 'og:image:height', content: String(item.height) });
          }
        }
      });
    } else if (media.url) {
      metaTags.push({ property: 'og:image', content: media.url });
      if (media.altText) {
        metaTags.push({ property: 'og:image:alt', content: media.altText });
      }
      if (media.width) {
        metaTags.push({ property: 'og:image:width', content: String(media.width) });
      }
      if (media.height) {
        metaTags.push({ property: 'og:image:height', content: String(media.height) });
      }
    }
  }

  // Robots meta
  if (robots) {
    const robotsContent = [];
    if (robots.noIndex) {
      robotsContent.push('noindex');
    }
    if (robots.noFollow) {
      robotsContent.push('nofollow');
    }
    if (robotsContent.length > 0) {
      metaTags.push({ name: 'robots', content: robotsContent.join(', ') });
    }
  }

  // JSON-LD structured data
  if (jsonLd) {
    const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    jsonLdArray.forEach((item) => {
      metaTags.push({
        'script:ld+json': item,
      });
    });
  }

  return metaTags;
}
